import React from 'react';
import { useAppContext } from '../contexts/AppContext';
import { Building2, FolderOpen, BookOpen } from 'lucide-react';
import { getStandardConfig } from '../constants/fmeaStandards';
import { FmeaStandard } from '../types';

interface ActiveProjectBadgeProps {
    showDetails?: boolean;
    showStandard?: boolean;
    className?: string;
}

// Helper function to get status color class
const STATUS_COLORS: Record<string, string> = {
    active: 'bg-green-100 text-green-700 border-green-300',
    inactive: 'bg-gray-100 text-gray-600 border-gray-300',
    archived: 'bg-yellow-100 text-yellow-700 border-yellow-300'
};

const getStatusColor = (status: string): string => STATUS_COLORS[status] ?? STATUS_COLORS.active;

// Hook to extract and derive badge data from context
const useBadgeData = () => {
    const { activeProject, allData } = useAppContext();
    const pm = allData.projectMetadata;

    return {
        displayName: pm?.displayName ?? activeProject,
        customer: pm?.customer ?? 'Unassigned',
        stage: pm?.stage ?? 'design',
        status: pm?.status ?? 'active',
        description: pm?.description,
        standardConfig: pm?.fmeaStandard ? getStandardConfig(pm.fmeaStandard as FmeaStandard) : null
    };
};

/**
 * Display badge showing the currently loaded project
 * Replaces ProjectSelector dropdowns in most apps to enforce persistence
 */
const ActiveProjectBadge: React.FC<ActiveProjectBadgeProps> = ({
    showDetails = false,
    showStandard = true,
    className = ''
}) => {
    const { displayName, customer, stage, status, description, standardConfig } = useBadgeData();
    const statusColor = getStatusColor(status);

    return showDetails ? (
        <DetailedBadge
            displayName={displayName}
            description={description}
            status={status}
            statusColor={statusColor}
            customer={customer}
            stage={stage}
            className={className}
        />
    ) : (
        <CompactBadge
            displayName={displayName}
            status={status}
            statusColor={statusColor}
            showStandard={showStandard}
            standardConfig={standardConfig}
            className={className}
        />
    );
};

// Sub-component: Compact badge view
const CompactBadge: React.FC<{
    displayName: string;
    status: string;
    statusColor: string;
    showStandard: boolean;
    standardConfig: ReturnType<typeof getStandardConfig> | null;
    className: string;
}> = ({ displayName, status, statusColor, showStandard, standardConfig, className }) => (
    <div className={`inline-flex items-center gap-3 ${className}`}>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/30 rounded-lg">
            <FolderOpen size={16} className="text-primary" />
            <span className="font-medium text-primary">{displayName}</span>
            {status !== 'active' && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${statusColor}`}>
                    {status}
                </span>
            )}
        </div>
        {showStandard && standardConfig && (
            <div className="inline-flex items-center gap-1.5 px-2 py-1.5 bg-secondary/10 border border-secondary/30 rounded-lg">
                <BookOpen size={12} className="text-secondary" />
                <span className="text-xs font-medium text-secondary">{standardConfig.name}</span>
            </div>
        )}
    </div>
);

// Sub-component: Detailed badge view
const DetailedBadge: React.FC<{
    displayName: string;
    description?: string;
    status: string;
    statusColor: string;
    customer: string;
    stage: string;
    className: string;
}> = ({ displayName, description, status, statusColor, customer, stage, className }) => (
    <div className={`bg-white border border-border rounded-lg p-4 ${className}`}>
        <div className="flex items-start justify-between mb-2">
            <div>
                <div className="flex items-center gap-2 mb-1">
                    <FolderOpen size={18} className="text-primary" />
                    <h3 className="font-bold text-text-primary text-lg">{displayName}</h3>
                </div>
                <p className="text-sm text-text-secondary">{description || 'No description'}</p>
            </div>
            <span className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase ${statusColor} border`}>
                {status}
            </span>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-border">
            <div className="flex items-center gap-2 text-sm">
                <Building2 size={14} className="text-text-secondary" />
                <span className="text-text-secondary">Customer:</span>
                <span className="font-medium">{customer}</span>
            </div>
            <div className="text-sm">
                <span className="text-text-secondary">Stage:</span>
                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold uppercase rounded-full">
                    {stage}
                </span>
            </div>
        </div>
    </div>
);

export default ActiveProjectBadge;
